import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Search, TrendingUp, Clock, Plus } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { studentMusicalsDiscoveryService } from '../../services/studentMusicalsDiscoveryService';
import { useRealtimeSync } from '../../hooks/useRealtimeSync';
import Header from '../../components/ui/Header';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

export default function StudentMusicalsDiscovery() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [musicals, setMusicals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Content state
  const [continueLearning, setContinueLearning] = useState([]);
  const [popularMusicals, setPopularMusicals] = useState([]);
  const [recentlyUpdated, setRecentlyUpdated] = useState([]);
  const [recentlyAdded, setRecentlyAdded] = useState([]);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);

  // ✅ Real-time sync for musicals - students see updates instantly
  useRealtimeSync('musicals', {
    onInsert: (newMusical) => {
      console.log('New musical available:', newMusical);
      // Show notification and refetch
      fetchMusicals();
    },
    onUpdate: (updatedMusical) => {
      console.log('Musical content updated:', updatedMusical);
      // Update the musical in the current view
      setMusicals(prevMusicals => 
        prevMusicals?.map(musical => 
          musical?.id === updatedMusical?.id ? { ...musical, ...updatedMusical } : musical
        )
      );
    },
    onDelete: (deletedMusical) => {
      console.log('Musical removed:', deletedMusical);
      // Remove from student's view
      setMusicals(prevMusicals => 
        prevMusicals?.filter(musical => musical?.id !== deletedMusical?.id)
      );
    }
  });

  const fetchMusicals = async () => {
    try {
      setLoading(true);
      setError('');

      const [continueData, popularData, updatedData, addedData] = await Promise.all([
        studentMusicalsDiscoveryService?.getContinueLearningMusicals(user?.id, 12),
        studentMusicalsDiscoveryService?.getPopularMusicals(12),
        studentMusicalsDiscoveryService?.getRecentlyUpdatedMusicals(12),
        studentMusicalsDiscoveryService?.getRecentlyAddedMusicals(12)
      ]);

      setContinueLearning(continueData);
      setPopularMusicals(popularData);
      setRecentlyUpdated(updatedData);
      setRecentlyAdded(addedData);
    } catch (err) {
      console.error('Error loading musicals:', err);
      setError(err?.message || 'Failed to load musicals');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.id) {
      fetchMusicals();
    }
  }, [user]);

  const handleSearch = async (e) => {
    e?.preventDefault();
    if (!searchQuery?.trim()) {
      setSearchResults(null);
      return;
    }

    try {
      setSearchLoading(true);
      const results = await studentMusicalsDiscoveryService?.searchMusicals(searchQuery);
      setSearchResults(results);
    } catch (err) {
      console.error('Error searching:', err);
      setError(err?.message || 'Search failed');
    } finally {
      setSearchLoading(false);
    }
  };

  const navigateToMusical = (musicalId) => {
    navigate(`/musical-progress-page?id=${musicalId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#6e32b9] to-[#32215d]">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Musicals - RoleCall Student Discovery</title>
        <meta name="description" content="Discover and explore musical theater productions with comprehensive learning pathways" />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-[#6e32b9] to-[#32215d]">
        <Header />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Error Display */}
          {error && (
            <div className="mb-6 bg-red-500/10 border border-red-500 rounded-xl p-4">
              <p className="text-red-200 text-sm">{error}</p>
            </div>
          )}

          {/* Search Bar */}
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 mb-8 border border-white/20">
            <form onSubmit={handleSearch} className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  placeholder="Search musicals..."
                  className="w-full bg-white/5 border border-white/20 rounded-xl pl-10 pr-4 py-3 text-white placeholder-white/50 focus:outline-none focus:border-purple-400"
                />
              </div>
              <button
                type="submit"
                disabled={searchLoading}
                className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-medium transition-colors disabled:opacity-50"
              >
                {searchLoading ? 'Searching...' : 'Search'}
              </button>
            </form>

            {/* Search Results */}
            {searchResults && (
              <div className="mt-6">
                <h3 className="text-white font-semibold mb-3">Search Results ({searchResults?.length})</h3>
                {searchResults?.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    {searchResults?.map((musical) => (
                      <div
                        key={musical?.id}
                        onClick={() => navigateToMusical(musical?.id)}
                        className="cursor-pointer hover:scale-105 transition-transform"
                      >
                        <img
                          src={musical?.posterUrl || '/assets/images/no_image.png'}
                          alt={musical?.title}
                          className="w-full aspect-[2/3] object-cover rounded-lg mb-2"
                        />
                        <p className="text-white text-sm font-medium truncate">{musical?.title}</p>
                        {musical?.genre && (
                          <p className="text-white/60 text-xs">{musical?.genre}</p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-white/70">No musicals found matching "{searchQuery}"</p>
                )}
              </div>
            )}
          </div>

          {/* Continue Learning */}
          {continueLearning?.length > 0 && (
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 mb-8 border border-white/20">
              <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <Clock className="w-6 h-6 text-blue-400" />
                Continue Learning
              </h2>
              <div className="overflow-x-auto">
                <div className="flex gap-4 pb-4">
                  {continueLearning?.map((musical) => (
                    <div
                      key={musical?.id}
                      onClick={() => navigateToMusical(musical?.id)}
                      className="flex-shrink-0 w-40 cursor-pointer hover:scale-105 transition-transform"
                    >
                      <img
                        src={musical?.posterUrl || '/assets/images/no_image.png'}
                        alt={musical?.title}
                        className="w-full aspect-[2/3] object-cover rounded-lg mb-2"
                      />
                      <p className="text-white text-sm font-medium truncate">{musical?.title}</p>
                      {musical?.genre && (
                        <p className="text-white/60 text-xs">{musical?.genre}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Popular Musicals */}
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 mb-8 border border-white/20">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <TrendingUp className="w-6 h-6 text-yellow-400" />
              Popular Musicals
            </h2>
            <div className="overflow-x-auto">
              <div className="flex gap-4 pb-4">
                {popularMusicals?.map((musical) => (
                  <div
                    key={musical?.id}
                    onClick={() => navigateToMusical(musical?.id)}
                    className="flex-shrink-0 w-40 cursor-pointer hover:scale-105 transition-transform"
                  >
                    <img
                      src={musical?.posterUrl || '/assets/images/no_image.png'}
                      alt={musical?.title}
                      className="w-full aspect-[2/3] object-cover rounded-lg mb-2"
                    />
                    <p className="text-white text-sm font-medium truncate">{musical?.title}</p>
                    {musical?.genre && (
                      <p className="text-white/60 text-xs">{musical?.genre}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Recently Updated Musicals */}
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 mb-8 border border-white/20">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Clock className="w-6 h-6 text-green-400" />
              Recently Updated Musicals
            </h2>
            <div className="overflow-x-auto">
              <div className="flex gap-4 pb-4">
                {recentlyUpdated?.map((musical) => (
                  <div
                    key={musical?.id}
                    onClick={() => navigateToMusical(musical?.id)}
                    className="flex-shrink-0 w-40 cursor-pointer hover:scale-105 transition-transform"
                  >
                    <img
                      src={musical?.posterUrl || '/assets/images/no_image.png'}
                      alt={musical?.title}
                      className="w-full aspect-[2/3] object-cover rounded-lg mb-2"
                    />
                    <p className="text-white text-sm font-medium truncate">{musical?.title}</p>
                    {musical?.genre && (
                      <p className="text-white/60 text-xs">{musical?.genre}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Recently Added Musicals */}
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Plus className="w-6 h-6 text-purple-400" />
              Recently Added Musicals
            </h2>
            <div className="overflow-x-auto">
              <div className="flex gap-4 pb-4">
                {recentlyAdded?.map((musical) => (
                  <div
                    key={musical?.id}
                    onClick={() => navigateToMusical(musical?.id)}
                    className="flex-shrink-0 w-40 cursor-pointer hover:scale-105 transition-transform"
                  >
                    <img
                      src={musical?.posterUrl || '/assets/images/no_image.png'}
                      alt={musical?.title}
                      className="w-full aspect-[2/3] object-cover rounded-lg mb-2"
                    />
                    <p className="text-white text-sm font-medium truncate">{musical?.title}</p>
                    {musical?.genre && (
                      <p className="text-white/60 text-xs">{musical?.genre}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}